import { expectDefined, immerable } from '@utils';
import { Elements } from './Elements';
import type { MutableGraph } from './interface/MutableGraph';
import { EdgeDirection, type EID, type Edges, type NID, type Nodes } from './types';

// #region Node

class Node<N> {
  static [immerable] = true;

  inEdges: Set<EID>;
  outEdges: Set<EID>;
  data?: N;

  constructor(data?: N) {
    this.inEdges = new Set();
    this.outEdges = new Set();
    this.data = data;
  }
}

// #endregion

// #region Edge

class Edge<E> {
  static [immerable] = true;

  source: NID;
  target: NID;
  data?: E;

  constructor(source: NID, target: NID, data?: E) {
    this.source = source;
    this.target = target;
    this.data = data;
  }
}

// #endregion

// #region Graph

export type BlankGraph = Graph<unknown, unknown, unknown>;

export class Graph<G, N, E> implements MutableGraph<G, N, E> {
  [immerable] = true;

  private _nodes = new Map<NID, Node<N>>();
  private _edges = new Map<EID, Edge<E>>();
  private _data?: G;

  private _roots: Nodes = new Set();
  private _leaves: Nodes = new Set();

  // #region Constructors

  constructor(opts?: { data?: G }) {
    this._data = opts?.data;
  }

  // #endregion

  // #region VisitableGraph

  get isEmpty(): boolean {
    return this._nodes.size === 0;
  }

  get nodeCount(): number {
    return this._nodes.size;
  }

  get edgeCount(): number {
    return this._edges.size;
  }

  get nodes(): Nodes {
    return new Set(this._nodes.keys());
  }

  get edges(): Edges {
    return new Set(this._edges.keys());
  }

  get elements(): Elements {
    return Elements.make(this.nodes, this.edges);
  }

  hasNode(id: NID): boolean {
    return this._nodes.has(id);
  }

  hasEdge(id: EID): boolean {
    return this._edges.has(id);
  }

  hasEdgeFromTo(source: NID, target: NID): boolean {
    const sourceNode = this._requireNode(source);
    this._requireNode(target);
    return Array.from(sourceNode.outEdges).some(
      edgeId => expectDefined(this._edges.get(edgeId)).target === target
    );
  }

  hasEdgeBetween(a: NID, b: NID): boolean {
    return this.hasEdgeFromTo(a, b) || this.hasEdgeFromTo(b, a);
  }

  source(id: EID): NID {
    return this._requireEdge(id).source;
  }

  target(id: EID): NID {
    return this._requireEdge(id).target;
  }

  endpoints(id: EID): [NID, NID] {
    const edge = this._requireEdge(id);
    return [edge.source, edge.target];
  }

  endpoint(id: EID, direction: EdgeDirection): NID {
    const edge = this._requireEdge(id);
    return direction === EdgeDirection.TAIL ? edge.source : edge.target;
  }

  outEdges(id: NID): Edges {
    return new Set(this._requireNode(id).outEdges);
  }

  inEdges(id: NID): Edges {
    return new Set(this._requireNode(id).inEdges);
  }

  incidentEdges(id: NID): Edges {
    return new Set([...this.inEdges(id), ...this.outEdges(id)]);
  }

  outDegree(id: NID): number {
    return this._requireNode(id).outEdges.size;
  }

  inDegree(id: NID): number {
    return this._requireNode(id).inEdges.size;
  }

  degree(id: NID): number {
    return this.outDegree(id) + this.inDegree(id);
  }

  successors(id: NID | Nodes): Nodes {
    if (id instanceof Set) {
      const result = new Set<NID>();

      for (const nodeId of id) {
        const node = this._requireNode(nodeId);
        for (const edgeId of node.outEdges) {
          result.add(expectDefined(this._edges.get(edgeId)).target);
        }
      }

      return result;
    } else {
      return new Set(
        Array.from(this.outEdges(id)).map(
          edgeId => expectDefined(this._edges.get(edgeId)).target
        )
      );
    }
  }

  transitiveSuccessors(id: NID | Nodes): Nodes {
    const result = new Set<NID>();

    let ids: Set<NID>;
    if (id instanceof Set) {
      ids = id;
    } else {
      ids = new Set([id]);
    }

    while (ids.size > 0) {
      const nextNodes = this.successors(ids);

      if (nextNodes.size === 0) {
        break;
      }

      let newNext = false;
      for (const node of nextNodes) {
        if (!result.has(node)) {
          result.add(node);
          newNext = true;
        }
      }

      if (!newNext) {
        break;
      }

      ids = nextNodes;
    }

    return result;
  }

  transitivePredecessors(id: NID | Nodes): Nodes {
    const result = new Set<NID>();

    let ids: Set<NID>;
    if (id instanceof Set) {
      ids = id;
    } else {
      ids = new Set([id]);
    }

    while (ids.size > 0) {
      const nextNodes = this.predecessors(ids);

      if (nextNodes.size === 0) {
        break;
      }

      let newNext = false;
      for (const node of nextNodes) {
        if (!result.has(node)) {
          result.add(node);
          newNext = true;
        }
      }

      if (!newNext) {
        break;
      }

      ids = nextNodes;
    }

    return result;
  }

  predecessors(id: NID | Nodes): Nodes {
    if (id instanceof Set) {
      const result = new Set<NID>();

      for (const nodeId of id) {
        const node = this._requireNode(nodeId);
        for (const edgeId of node.inEdges) {
          result.add(expectDefined(this._edges.get(edgeId)).source);
        }
      }

      return result;
    } else {
      return new Set(
        Array.from(this.inEdges(id)).map(
          edgeId => expectDefined(this._edges.get(edgeId)).source
        )
      );
    }
  }

  neighbors(id: NID): Nodes {
    return new Set([...this.successors(id), ...this.predecessors(id)]);
  }

  hasSuccessors(id: NID): boolean {
    return this.outDegree(id) > 0;
  }

  hasPredecessors(id: NID): boolean {
    return this.inDegree(id) > 0;
  }

  hasNeighbors(id: NID): boolean {
    return this.degree(id) > 0;
  }

  get data(): G {
    return this._data as G;
  }

  nodeData(id: NID): N {
    const data = this._requireNode(id).data;
    return data as N;
  }

  edgeData(id: EID): E {
    const data = this._requireEdge(id).data;
    return data as E;
  }

  get roots(): Nodes {
    return new Set(this._roots);
  }

  get leaves(): Nodes {
    return new Set(this._leaves);
  }

  get nonRoots(): Nodes {
    return this.nodes.difference(this.roots);
  }

  get nonLeaves(): Nodes {
    return this.nodes.difference(this.leaves);
  }

  get internals(): Nodes {
    return this.nodes.difference(this.roots.union(this.leaves));
  }

  isRoot(id: NID): boolean {
    return this._requireNode(id).inEdges.size === 0;
  }

  isLeaf(id: NID): boolean {
    return this._requireNode(id).outEdges.size === 0;
  }

  isInternal(id: NID): boolean {
    const node = this._requireNode(id);
    return node.inEdges.size > 0 && node.outEdges.size > 0;
  }

  // #endregion

  // #region MutableGraph

  setData(data: G): this {
    this._data = data;
    return this;
  }

  setNodeData(id: NID, data: N): this {
    this._requireNode(id).data = data;
    return this;
  }

  setEdgeData(id: EID, data: E): this {
    this._requireEdge(id).data = data;
    return this;
  }

  withData(f: (data: G) => void): this {
    f(this._data as G);
    return this;
  }

  withNodeData(id: NID, f: (data: N) => void): this {
    f(this._requireNode(id).data as N);
    return this;
  }

  withEdgeData(id: EID, f: (data: E) => void): this {
    f(this._requireEdge(id).data as E);
    return this;
  }

  addNode(id: NID, opts?: { data?: N }): this {
    this._requireNoNode(id);
    this._nodes.set(id, new Node(opts?.data));
    this._roots.add(id);
    this._leaves.add(id);
    return this;
  }

  addEdge(id: EID, source: NID, target: NID, data?: E): this {
    this._requireNoEdge(id);
    this._requireNode(source);
    this._requireNode(target);
    this._addEdge(id, source, target, data);
    return this;
  }

  removeNode(id: NID): this {
    this._requireNode(id);
    this._clearEdges(id);
    this._nodes.delete(id);
    this._roots.delete(id);
    this._leaves.delete(id);
    return this;
  }

  removeEdge(id: EID): this {
    this._requireEdge(id);
    this._removeEdge(id);
    return this;
  }

  clearEdges(id: NID): this {
    this._requireNode(id);
    this._clearEdges(id);
    return this;
  }

  moveEdge(id: EID, ends: { newSource?: NID; newTarget?: NID }): this {
    const edge = this._requireEdge(id);
    const { source, target } = edge;
    const { newSource, newTarget } = ends;
    const nextSource = newSource ?? source;
    const nextTarget = newTarget ?? target;
    if (source === nextSource && target === nextTarget) {
      return this;
    }
    this._requireNode(nextSource);
    this._requireNode(nextTarget);
    this._removeEdge(id);
    this._addEdge(id, nextSource, nextTarget, edge.data);
    return this;
  }

  // #endregion

  // #region Private

  private _requireNode(id: NID): Node<N> {
    const node = this._nodes.get(id);
    if (!node) {
      throw new Error(`Node '${id}' not found`);
    }
    return node;
  }

  private _requireNoNode(id: NID): void {
    if (this._nodes.has(id)) {
      throw new Error(`Node '${id}' already exists`);
    }
  }

  private _requireEdge(id: EID): Edge<E> {
    const edge = this._edges.get(id);
    if (!edge) {
      throw new Error(`Edge '${id}' not found`);
    }
    return edge;
  }

  private _requireNoEdge(id: EID): void {
    if (this._edges.has(id)) {
      throw new Error(`Edge '${id}' already exists`);
    }
  }

  private _addEdge(id: EID, source: NID, target: NID, data?: E): void {
    const edge = new Edge(source, target, data);
    this._edges.set(id, edge);
    expectDefined(this._nodes.get(source)).outEdges.add(id);
    expectDefined(this._nodes.get(target)).inEdges.add(id);
    this._leaves.delete(source);
    this._roots.delete(target);
  }

  private _removeEdge(id: EID): void {
    const edge = expectDefined(this._edges.get(id));
    const source = expectDefined(this._nodes.get(edge.source));
    const target = expectDefined(this._nodes.get(edge.target));
    source.outEdges.delete(id);
    target.inEdges.delete(id);
    this._edges.delete(id);
    if (source.outEdges.size === 0) {
      this._leaves.add(edge.source);
    }
    if (target.inEdges.size === 0) {
      this._roots.add(edge.target);
    }
  }

  private _clearEdges(id: NID): void {
    for (const edgeId of Array.from(expectDefined(this._nodes.get(id)).inEdges)) {
      this._removeEdge(edgeId);
    }
    for (const edgeId of Array.from(expectDefined(this._nodes.get(id)).outEdges)) {
      this._removeEdge(edgeId);
    }
  }

  // #endregion
}

// #endregion
